// FindingsConsole: Findings as a queue the team works. Each row is a case
// (useInboxCases: what needs a person, joined across sessions); the list is
// filtered by who holds a case (nobody, which is triage; the reader; anyone;
// one person), named once, by the heading that picks it, with closed cases
// shown when asked for; a case opens in place, under its row, one at a time
// and only when the reader opens it; and every decision is saved as the
// case's finding investigation, so the whole team sees it, with who did it
// and when (lib/findingsConsole.js, lib/useConsoleInvestigations.js). Nothing
// is a true positive until an investigation says so: triage investigates a
// case, rejects it (a false positive, expected activity, a duplicate of an
// investigation, which it joins, or another reason) or assigns it. Several
// selected take a decision together.
//
// The search is Explore's query language, which narrows the sessions the
// findings are read from, plus the case's own fields (standard, severity,
// owner, state), which narrow the cases.
import { buttonClass } from "./ui/Button.jsx";
import Checkbox from "./ui/Checkbox.jsx";
import Highlight from "./ui/Highlight.jsx";
import SearchInput from "./ui/SearchInput.jsx";
import Tabs from "./ui/Tabs.jsx";
import { FIELD as UI_FIELD, MENU } from "./ui/styles.js";
import { CheckIcon } from "@heroicons/react/24/outline";
import { Fragment, memo, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ScrollArea from "./ScrollArea.jsx";
import DateRangePicker from "./DateRangePicker.jsx";
import Select from "./Select.jsx";
import CaseStory from "./CaseStory.jsx";
import { SeverityWord } from "./lenses/triageParts.jsx";
import { machineOf, useSessionIdentity } from "./lenses/reviewSessions.jsx";
import { useCaseTuning, useInboxCases } from "./CaseInbox.jsx";
import FindingsTime from "./FindingsTime.jsx";
import { inRange } from "../lib/findingsTime.js";
import { experimentalEnabled } from "../lib/featureFlags.js";
import { instructionOf } from "../lib/findingsTriage.js";
import { openInboxCase } from "../lib/caseInboxWork.js";
import { maskSecrets } from "../lib/findingSummary.js";
import { bytesBase64ToUuid } from "../lib/reviewClient.js";
import { parseServerTimestamp } from "../lib/traceUtils.js";
import { caseView } from "../lib/investigateCases.js";
import { GROUP_LABEL, GROUP_ORDER, GROUP_SHORT } from "../lib/investigateFacts.js";
import {
  ASSIGNEES, ASSIGNEE_LABEL, CLOSE_REASONS, QUICK, QUICK_LABEL, SORTS, SORT_LABEL,
  canDecide, consoleEntries, filterFor, filterTitle, inFilter, isOpenState, notCheckedOf, ownerLabel, passesQuick, readNote, reasonFromNote, caseSearchText, passesWords, searchWords, sortEntries, withEventTimes,
} from "../lib/findingsConsole.js";
import { useEventTimes } from "../lib/findingEventTimes.js";
import { keepCases, setConsole, useConsole } from "../lib/findingsConsoleStore.js";
import { useConsoleInvestigations, useInvestigationActivity } from "../lib/useConsoleInvestigations.js";
import { useLabels } from "../lib/dimensionDefs.js";
import { readTraceNames } from "../lib/traceNames.js";
import { caseWordsOf, useCaseWordsVersion } from "../lib/caseWords.js";
import { useWalkthroughsVersion, walkthroughOf } from "../lib/traceWalkthrough.js";
import { hasPermission, sensitiveAccess } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { ROUTER_PARAM } from "../lib/routeModel.js";
import { REVIEW_SESSION_CAP } from "../lib/scopeReviews.js";
import { useCurrentUser } from "../lib/userInfo.js";
import { setWorkspaceSearch } from "../lib/workspaceSearch.js";

const NUM = new Intl.NumberFormat();
const lower = (v) => String(v ?? "").trim().toLowerCase();
const AT = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
const stamp = (ms) => (Number.isFinite(ms) ? AT.format(ms) : "");
const plural = (n, one, many = `${one}s`) => `${NUM.format(n)} ${n === 1 ? one : many}`;
// The reader as the investigation API names users, a UUID: /v1/me sends the
// id's bytes base64-encoded (other readers of it keep that form).
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const asUserId = (id) => (!id ? null : UUID.test(id) ? lower(id) : bytesBase64ToUuid(id) || id);
// A refusal's words as a sentence, so "Try again" follows a full stop.
const sentence = (text) => (/[.!?]$/.test(text) ? text : `${text}.`);

const BUTTON = buttonClass("secondary");
const PRIMARY = buttonClass("primary");
// The verdicts close a case, so they sit together as one segmented control,
// apart from the quieter actions that only hand a case on or annotate it.
const VERDICTS = "inline-flex overflow-hidden rounded-[7px] border border-[color:var(--line-strong)]";
const SEGMENT = "inline-flex h-[32px] cursor-pointer items-center gap-[8px] whitespace-nowrap border-0 border-l border-[color:var(--line-strong)] bg-transparent px-[12px] text-[13px] text-[color:var(--ink)] transition-colors duration-150 first:border-l-0 hover:bg-[color-mix(in_srgb,var(--ink)_8%,transparent)] disabled:cursor-default disabled:opacity-50";
const verdict = (label, color) => <><span aria-hidden="true" className={`size-[8px] rounded-full ${color}`} />{label}</>;
const QUIET = buttonClass("ghost");
const EYEBROW = "m-0 text-[12px] font-medium text-[color:var(--muted)]";
const BAR_LABEL = "mr-[4px] text-[12.5px] text-[color:var(--muted)]";
// A filter as a key, so a change of it is seen.
const filterKeyOf = (f) => `${f.assignee}|${!!f.closed}|${!!f.cleared}`;
// Why a rare case (an act in one session no standard caught) is listed, on its "No standard met".
const ONLY_SESSION_WHY = "Shown because no other trace has this act.";
// What Reject closes a case as: a false positive (the detection was wrong),
// expected activity, a duplicate of an open investigation (which it joins),
// or a reason of the analyst's own.
const REJECTS = ["wrong", "expected", "duplicate", "other"];
const REJECT_LABEL = { wrong: "False positive", expected: "Expected activity", duplicate: "Duplicate", other: "Other reason" };
// What a decision's toast says, about one case or several.
const SAID = {
  investigate: (w, n) => `${w} ${n > 1 ? "are" : "is"} yours to investigate`,
  together: (w) => `Joined ${w} into one investigation, yours`,
  tp: (w, n) => `Closed ${w} as ${n > 1 ? "true positives" : "a true positive"}`,
  fp: (r) => (w, n) => `Marked ${w} ${n > 1 ? "false positives" : "a false positive"}: ${CLOSE_REASONS[r].toLowerCase()}`,
  join: (title) => (w, n) => `Added ${w} to \u201c${title}\u201d, as ${n > 1 ? "duplicates" : "a duplicate"}`,
  inconclusive: (w) => `Closed ${w} as inconclusive`,
  reject: (w) => `Rejected ${w}`,
  putBack: (w) => `Put ${w} back in triage`,
  note: (w) => `Noted on ${w}`,
  reopen: (w, n) => `Reopened ${w}. ${n > 1 ? "They're" : "It's"} back in triage.`,
};
// The prompts that ask for the analyst's words before a decision is saved.
// A close also asks, optionally, what was not checked (`notChecked`).
const FP_NOTE = (r) => ({ kind: "note", action: "close", opts: { reason: r }, ask: `False positive: ${CLOSE_REASONS[r].toLowerCase()}`, hint: "Why", label: SAID.fp(r), notChecked: true });
const TP_NOTE = { kind: "note", action: "truePositive", opts: {}, ask: "True positive", hint: "Response", label: SAID.tp, notChecked: true };
const INCONCLUSIVE_NOTE = { kind: "note", action: "inconclusive", opts: {}, ask: "Inconclusive", hint: "What is unresolved", label: SAID.inconclusive, notChecked: true };
const NOTE = { kind: "note", action: "note", opts: {}, ask: "Note", hint: "Note", label: SAID.note, required: true };
// What each of Reject's reasons asks before it is saved: why, optionally,
// and for a reason of the analyst's own, what it is.
const REJECT_NOTE = {
  wrong: FP_NOTE("wrong"),
  expected: FP_NOTE("expected"),
  other: { kind: "note", action: "close", opts: { reason: "other" }, ask: "Rejected", hint: "Why", label: SAID.reject, required: true, notChecked: true },
};

// Where the team left a case, in words.
function stateWords(entry, ownerOf) {
  const who = entry.owner ? ownerOf(entry.owner) : "";
  const by = who === "You" ? " by you" : who ? ` by ${who}` : "";
  if (entry.state === "new") return entry.fresh ? `${plural(entry.fresh, "new finding")} since closed` : "New";
  if (entry.state === "investigating") return who === "You" ? "You're investigating" : `${who} is investigating`;
  if (entry.state === "tp") return by ? `True positive, closed${by}` : "True positive";
  if (entry.state === "joined") return `Duplicate of \u201c${entry.joined.title}\u201d`;
  if (entry.reason === "mixed") return `Mixed outcome${by}`;
  if (entry.reason === "inconclusive") return by ? `Inconclusive, closed${by}` : "Inconclusive";
  if (entry.reason === "other") return `Rejected${by}`;
  if (entry.state === "cleared") return `Cleared: ${GROUP_SHORT[entry.cleared] || "by review"}`;
  return entry.reason ? `False positive: ${CLOSE_REASONS[entry.reason].toLowerCase()}` : "False positive";
}

// What one activity entry says the person did (`says`), and what they wrote
// with it (`words`), masked.
const NOTE_SAYS = {
  truePositive: "closed it as a true positive",
  inconclusive: "closed it as inconclusive",
  resolve: "resolved it",
  confirm: "confirmed it: it needs action",
  expected: "marked it a false positive: expected activity",
  wrong: "marked it a false positive: the detection was wrong",
  duplicate: "marked it a false positive: a duplicate of another case",
  other: "rejected it",
  reopen: "reopened it",
};
function activityLine(e, ownerOf) {
  const said = e.note ? readNote(e.note) : null;
  const words = (text) => maskSecrets(text || "");
  if (said?.key === "added") return { says: `added ${plural(e.added, "finding")} from a duplicate, \u201c${words(said.text)}\u201d`, words: "" };
  if (said?.key === "joined") return { says: `marked it a duplicate of \u201c${words(said.text)}\u201d`, words: "" };
  if (said?.key === "saved") return e.added ? { says: `added the ${plural(e.added, "finding")} the case had gained`, words: "" } : null;
  if (said) {
    const { words: own, notChecked } = notCheckedOf(said.text);
    return { says: NOTE_SAYS[said.key], words: words(own), notChecked: words(notChecked) };
  }
  if (e.note) return { says: "noted", words: words(e.note) };
  if (e.added && e.ownerUserId) return { says: e.ownerUserId === e.actorUserId ? `took it, saving ${plural(e.added, "finding")}` : `assigned it to ${ownerOf(e.ownerUserId)}, saving ${plural(e.added, "finding")}`, words: "" };
  if (e.added) return { says: `saved it with ${plural(e.added, "finding")}`, words: "" };
  if (e.removed) return { says: `took back ${plural(e.removed, "finding")}`, words: "" };
  if (e.setAside) return { says: `set aside ${plural(e.setAside, "finding")}`, words: "" };
  if (e.restored) return { says: `restored ${plural(e.restored, "finding")}`, words: "" };
  if (e.status === "open") return { says: !e.ownerUserId ? "put it back in triage" : e.ownerUserId === e.actorUserId ? "took it" : `assigned it to ${ownerOf(e.ownerUserId)}`, words: "" };
  return { says: e.status === "closed" ? "closed it" : "changed it", words: "" };
}

// The case in one plain sentence: the first of its findings the checks
// described (why a standard met it, else what the verifier found), masked.
function peekOf(entry, verdicts) {
  for (const p of entry.view.items) {
    const v = verdicts?.[p.finding.id] || p.verdict;
    const text = v?.standardWhy || v?.correctedClaim || v?.why;
    if (text) return maskSecrets(text);
  }
  return "";
}

// The list's columns, shared by its rows and the placeholders that stand in
// for them; a phone drops the assignee and the time.
const ROW_GRID = "grid grid-cols-[18px_12px_62px_minmax(160px,1fr)_128px_92px] items-center gap-x-[12px] pl-[12px] pr-[16px] max-[640px]:grid-cols-[18px_12px_62px_minmax(0,1fr)]";

// A placeholder for text still being read.
function Bar({ className = "" }) {
  return <span aria-hidden="true" className={`block h-[9px] animate-pulse rounded-[2px] bg-[color:var(--line-soft)] motion-reduce:animate-none ${className}`} />;
}

// The list while it is read: rows the shape of a case's, each fainter than
// the last.
const LOADING_ROWS = 8;
function RowsLoading() {
  return (
    <div aria-hidden="true" data-console-loading="">
      {Array.from({ length: LOADING_ROWS }, (_, i) => (
        <div key={i} className={`${ROW_GRID} min-h-[52px] border-b border-[color:var(--line)]`} style={{ opacity: 1 - i * 0.1 }}>
          <span className="h-[16px] w-[16px] rounded-[4px] border border-[color:var(--line)]" />
          <span />
          <Bar className="w-[34px]" />
          <span className="grid gap-[7px]">
            <Bar className="w-[85%]" />
            <Bar className="w-[70%]" />
          </span>
          <Bar className="w-[72px] max-[640px]:hidden" />
          <Bar className="ml-auto w-[64px] max-[640px]:hidden" />
        </div>
      ))}
    </div>
  );
}

function Chevron({ open }) {
  return (
    <svg viewBox="0 0 10 10" aria-hidden="true" className={`h-[10px] w-[10px] self-center text-[color:var(--muted-soft)] transition-transform duration-[160ms] ${open ? "rotate-90" : ""}`} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3.5 2l3 3-3 3" />
    </svg>
  );
}

// One case in the list: its severity and title, with where the team left it
// (where that says more than that it is new) and what it is about under them,
// then when it happened. An open case wears the panel ground, a turned chevron and its
// whole title, and its body says the rest; the case the time strip names is
// lit. A row renders again only when its own props change, so a long list
// stays quick.
const CaseRow = memo(function CaseRow({ entry, open, lit = false, sel, peek, terms, ownerOf, onOpen, onSelect }) {
  const n = entry.view.findingIds.length;
  const mine = entry.state === "investigating" && ownerOf(entry.owner) === "You";
  const done = !isOpenState(entry.state);
  const state = entry.state === "new" && !entry.fresh ? "" : stateWords(entry, ownerOf);
  return (
    <div
      aria-expanded={open}
      data-console-row={entry.id}
      data-console-open={open ? "" : undefined}
      onClick={(ev) => onOpen(entry, ev.currentTarget)}
      className={[
        `${ROW_GRID} relative min-h-[40px] scroll-mt-[72px] cursor-pointer border-b text-[13px] transition-colors duration-150`,
        open ? "border-transparent bg-[color:var(--panel)]" : lit ? "border-[color:var(--line)] bg-[color-mix(in_srgb,var(--ink)_10%,transparent)]" : "border-[color:var(--line)] hover:bg-[color:var(--gray-fill)]",
      ].join(" ")}
    >
      <button
        type="button"
        aria-label={sel ? "Unselect" : "Select"}
        aria-pressed={sel}
        onClick={(e) => { e.stopPropagation(); onSelect(entry); }}
        className={`mt-[12px] grid h-[16px] w-[16px] cursor-pointer place-items-center self-start rounded-[4px] border transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[color:var(--green)] ${sel ? "border-[color:var(--green)] bg-[color:var(--green)]" : "border-[color:var(--line-strong)] bg-transparent"}`}
        data-console-select={entry.id}
      >
        {sel ? <CheckIcon aria-hidden="true" strokeWidth={3} className="h-[11px] w-[11px] text-[color:var(--bg)]" /> : null}
      </button>
      {/* Every cell sits level with the title's first line (20px down), so the
          line under it can go when the case opens without moving anything. */}
      <span className="mt-[15px] flex self-start"><Chevron open={open} /></span>
      <span className="mt-[12.5px] flex self-start"><SeverityWord severity={entry.severity} quiet={done} /></span>
      <span className="grid min-w-0 gap-[1px] self-start pb-[6px] pt-[10px]">
        <span className={`flex min-w-0 items-baseline gap-[6px] leading-[20px] ${done ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}>
          <span className="truncate"><Highlight text={entry.title} terms={terms} /></span>
          {n > 1 ? <span className="shrink-0 text-[11.5px] text-[color:var(--muted)]">{plural(n, "finding")}</span> : null}
        </span>
        {/* Open, the case says these. */}
        {!open && (state || peek) ? (
          <span className="truncate text-[12px] text-[color:var(--muted)]">
            {state ? <span className={`${peek ? "mr-[10px] " : ""}${mine ? "font-medium text-[color:var(--green)]" : entry.fresh ? "font-medium text-[color:var(--ink)]" : ""}`} data-console-row-state=""><Highlight text={state} terms={terms} /></span> : null}
            {peek ? <span data-console-peek=""><Highlight text={peek} terms={terms} /></span> : null}
          </span>
        ) : null}
      </span>
      {/* Kept open, so the row does not move; a phone has room for the title alone. */}
      <span className={`mt-[10px] self-start truncate text-[12px] leading-[20px] max-[640px]:hidden ${entry.owner ? "text-[color:var(--ink)]" : "text-[color:var(--muted-soft)]"}`} data-console-row-assignee="">{entry.owner ? ownerOf(entry.owner) : "Unassigned"}</span>
      <span className="mt-[10px] self-start whitespace-nowrap text-right text-[12px] leading-[20px] tabular-nums text-[color:var(--muted-soft)] max-[640px]:hidden" data-console-row-time="">{stamp(entry.latestAt)}</span>
    </div>
  );
});

export default function FindingsConsole({ t, caps, scopePath = [], range = "1M" }) {
  const [params, setParams] = useSearchParams();
  const c = useConsole();
  const user = useCurrentUser();
  const me = asUserId(user?.userId);
  // Origin's staff may list what review cleared, to account for what triage
  // drops; nobody else sees it.
  const staff = !!user?.isPlatformAdmin;
  const showCleared = staff && !!c.cleared;
  const access = sensitiveAccess(caps, PERMISSIONS.AI_SESSIONS_READ_SENSITIVE);
  const sensitive = access.status === "allowed";
  const canWrite = hasPermission(caps, PERMISSIONS.ALERT_TRIAGE_WRITE) || hasPermission(caps, PERMISSIONS.USERS_MANAGE);
  const canInvestigate = sensitive && hasPermission(caps, PERMISSIONS.ANALYTICS_EXECUTE);
  const canExplore = hasPermission(caps, PERMISSIONS.ANALYTICS_EXECUTE);
  // Explore opens only where the experimental flag shows it; elsewhere a mode=explore link lands on the Overview.
  const opensExplore = canExplore && experimentalEnabled();
  // Only what triage escalated, an audit flagged, or someone kept or opened an
  // investigation on (t.keepIds): no rare acts.
  const { cases, placed } = useInboxCases(t, { rare: false, keep: t.keepIds });
  const invs = useConsoleInvestigations({ enabled: true, canWrite });
  const tuningOf = useCaseTuning();

  const ownerOf = useCallback((id) => ownerLabel(id, invs.users, me), [invs.users, me]);
  const ownerSearchName = useCallback((id) => `${ownerLabel(id, invs.users, me)} ${invs.users.get(id) || ""}`, [invs.users, me]);
  const caseOf = useCallback((p) => caseView({ id: `r:${lower(p.finding.id)}`, title: "", notes: [], related: [], matches: [] }, [p]), []);
  const raw = useMemo(() => (cases ? consoleEntries({ cases, placed, byId: invs.byId, caseOf, cleared: showCleared }) : null), [cases, placed, invs.byId, caseOf, showCleared]);
  const setRange = useCallback((value) => setParams((current) => {
    const next = new URLSearchParams(current);
    next.set(ROUTER_PARAM.RANGE, value);
    return next;
  }), [setParams]);
  const filter = useMemo(() => ({ assignee: c.assignee, closed: c.closed, cleared: showCleared }), [c.assignee, c.closed, showCleared]);
  // A case the reader just took or assigned is kept where it was (`c.kept`).
  const kept = useMemo(() => new Set(c.kept), [c.kept]);
  // When the open case's acts happened, from the messages its findings cite
  // (when review finished says nothing about the act), at one read per session.
  const readWindow = t.lite.readWindow || t.ids.window;
  const timedId = lower(params.get(ROUTER_PARAM.TRIAGE_FINDING) || "");
  const eventFindings = useMemo(() => {
    if (!timedId || !raw) return [];
    const open = raw.find((e) => lower(e.id) === timedId) || raw.find((e) => e.view.findingIds.some((f) => lower(f) === timedId));
    return open ? open.view.items.map((p) => p.finding) : [];
  }, [raw, timedId]);
  const messageAt = useEventTimes(eventFindings, readWindow);
  // Harness only: a recorded month of real cases (lanes-harness/*.local.json),
  // so the page is judged against real spreads.
  const fixture = typeof globalThis.__originFindingsFixture === "object" ? globalThis.__originFindingsFixture : null;
  // A recorded month is held to the range here, as the list's read is.
  const entries = useMemo(() => (fixture ? fixture.entries.filter((e) => inRange(e.latestAt ?? e.firstAt, range)) : withEventTimes(raw, messageAt)), [fixture, raw, messageAt, range]);
  // The agents and models the listed traces ran, as Involved names them, for
  // the search: one read once the list is, for a reader who may explore.
  const sessionIds = useMemo(() => [...new Set((entries || []).flatMap((e) => e.view.sessions.map(lower)).filter(Boolean))].sort().join(","), [entries]);
  const [traceNames, setTraceNames] = useState(null);
  useEffect(() => {
    setTraceNames(null);
    if (!canExplore || !sessionIds || fixture) return undefined;
    let live = true;
    readTraceNames({ sessions: sessionIds.split(","), range }).then((names) => { if (live) setTraceNames(names); }, (err) => { console.warn("Findings search could not read the traces' agents and models.", err); });
    return () => { live = false; };
  }, [canExplore, sessionIds, range, fixture]);
  // What the search looks through, put together once per case (`labelTick`:
  // endpoint names landing; and what a case showed once opened, or its
  // walkthrough kept from before), so a keystroke only matches words.
  const labelTick = useLabels([]);
  const shownTick = useCaseWordsVersion();
  const walkTick = useWalkthroughsVersion();
  const searchTexts = useMemo(() => new Map((entries || []).map((e) => [e.id, caseSearchText(e, {
    peek: sensitive ? peekOf(e, t.liteShown.verdicts) : "",
    texts: sensitive ? e.view.items.flatMap((p) => {
      const v = t.liteShown.verdicts?.[p.finding.id] || p.verdict;
      return [p.finding.title, v?.standardWhy, v?.correctedClaim, v?.why].filter(Boolean).map(maskSecrets);
    }).concat(walkthroughOf(e.view.items[0]?.finding.session).notes.map((n) => maskSecrets(String(n.text || "")))) : [],
    shown: caseWordsOf(e.id),
    names: e.view.sessions.flatMap((id) => traceNames?.get(lower(id)) || []),
    machine: fixture ? fixture.machines[lower(e.view.endpoints[0])] : machineOf(e.view.endpoints[0]),
    state: stateWords(e, ownerOf),
    owner: e.owner ? ownerSearchName(e.owner) : "",
  })])), [entries, sensitive, t.liteShown.verdicts, traceNames, fixture, ownerOf, ownerSearchName, labelTick, shownTick, walkTick]);
  const words = useMemo(() => searchWords(c.query), [c.query]);
  const listed = useMemo(() => (entries
    ? entries.filter((e) => (inFilter(e, filter, me) || kept.has(e.id)) && passesWords(searchTexts.get(e.id) || "", words))
    : []), [entries, filter, kept, words, searchTexts, me]);
  const dayStart = new Date().setHours(0, 0, 0, 0);
  // The quick filters count the filter's own cases, not one kept in sight after a decision.
  const quickCounts = useMemo(() => Object.fromEntries(QUICK.map((q) => [q, listed.filter((e) => inFilter(e, filter, me) && passesQuick(e, q, dayStart)).length])), [listed, filter, me, dayStart]);
  const shown = useMemo(() => sortEntries(listed.filter((e) => passesQuick(e, c.quick, dayStart)), c.sort), [listed, c.quick, c.sort, dayStart]);
  // The case the time strip names, lit in the list.
  const [lit, setLit] = useState(null);
  // In the All view, by severity, a severity section folds away as an inbox
  // section does; what the keys move through and open is what is left in view.
  const foldable = c.quick === "all" && c.sort === "severity";
  const folded = useMemo(() => new Set(foldable ? c.folded : []), [foldable, c.folded]);
  const visible = useMemo(() => (folded.size ? shown.filter((e) => !folded.has(e.severity || "other")) : shown), [shown, folded]);
  const fold = useCallback((sev) => setConsole({ folded: c.folded.includes(sev) ? c.folded.filter((x) => x !== sev) : [...c.folded, sev] }), [c.folded]);

  // The assignee menu: whose cases the list can show, each with how many it
  // would list as closed cases show now; the people are those who hold one,
  // the person chosen always among them. Null while a read is under way.
  const assignees = useMemo(() => {
    if (!entries) return null;
    const n = (assignee) => entries.filter((e) => inFilter(e, { assignee, closed: c.closed }, me)).length;
    const people = new Map();
    for (const e of entries) if (e.owner && e.owner !== me && inFilter(e, { assignee: e.owner, closed: c.closed }, me)) people.set(e.owner, (people.get(e.owner) || 0) + 1);
    if (!ASSIGNEES.includes(c.assignee) && !people.has(c.assignee)) people.set(c.assignee, 0);
    return [
      ...ASSIGNEES.map((a) => [a, ASSIGNEE_LABEL[a], n(a)]),
      ...[...people.entries()].map(([id, k]) => [id, ownerLabel(id, invs.users, me), k]).sort((a, b) => b[2] - a[2] || a[1].localeCompare(b[1])),
    ];
  }, [entries, c.closed, c.assignee, me, invs.users]);

  // The case open beside the list rides the URL, so Back closes it. An old
  // link naming a finding opens the case that holds it.
  const asked = params.get(ROUTER_PARAM.TRIAGE_FINDING);
  const openEntry = useMemo(() => {
    if (!asked || !entries) return null;
    const id = lower(asked);
    return entries.find((e) => lower(e.id) === id)
      || entries.find((e) => e.view.findingIds.some((f) => lower(f) === id)) || null;
  }, [asked, entries]);
  const [sel, setSel] = useState(() => new Set());
  const [mode, setMode] = useState(null);
  // A case is open only while it is listed, and never beside a selection, so
  // a decision acts on what is in sight: the selection, else the case open.
  const isOpen = !!openEntry && !sel.size && visible.some((e) => e.id === openEntry.id);
  const cur = isOpen ? openEntry : null;
  // A prompt (why a false positive, whose a true positive is, who to assign)
  // lasts while what it acts on is in sight.
  const liveMode = isOpen || sel.size ? mode : null;
  const { loadNames } = invs;
  useEffect(() => { if (liveMode === "assign") loadNames(); }, [liveMode, loadNames]);
  // Arriving on a link to a case the filter leaves out goes to a filter that
  // lists it, once the reader is known (whose a case is turns on who they
  // are); a decision that moves the open case out of the list does not.
  const arrived = useRef(false);
  useEffect(() => {
    if (arrived.current || !entries || !me) return;
    arrived.current = true;
    if (openEntry && !inFilter(openEntry, filter, me)) setConsole(filterFor(openEntry, me));
  }, [entries, openEntry, filter, me]);

  // Opening a case from the list is a step Back undoes; moving between cases
  // and closing replace it. Opening a case ends a selection.
  const setOpen = useCallback((entry) => {
    if (entry) setSel((s) => (s.size ? new Set() : s));
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (entry) next.set(ROUTER_PARAM.TRIAGE_FINDING, entry.id); else next.delete(ROUTER_PARAM.TRIAGE_FINDING);
      return next;
    }, { replace: !!asked });
  }, [setParams, asked]);

  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const undoStack = useRef([]);
  const rootRef = useRef(null);
  const toastTimer = useRef(null);
  const say = useCallback((msg, extra = {}) => {
    clearTimeout(toastTimer.current);
    setToast({ msg, ...extra });
    toastTimer.current = setTimeout(() => setToast(null), extra.tune ? 9000 : extra.error ? 8000 : 5000);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);
  // A new filter or search starts the selection over.
  useEffect(() => { setSel(new Set()); setMode(null); }, [c.assignee, c.closed, c.query, c.quick]);
  // Selecting closes the open case, so it does not open again by itself when
  // the selection ends.
  const selecting = sel.size > 0;
  useEffect(() => { if (selecting && asked) setOpen(null); }, [selecting]); // eslint-disable-line react-hooks/exhaustive-deps
  // A filter the reader goes to starts at its top, and a case open outside it
  // closes (one a link arrived on is in the filter it went to, and stays).
  const filterKey = filterKeyOf(filter);
  const lastFilter = useRef(filterKey);
  useEffect(() => {
    if (lastFilter.current === filterKey) return;
    lastFilter.current = filterKey;
    if (openEntry && !inFilter(openEntry, filter, me)) setOpen(null);
  }, [filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const sessions = useMemo(() => [...new Set(shown.slice(0, 300).map((e) => lower(e.view.sessions[0])).filter(Boolean))], [shown]);
  const endpoints = useMemo(() => [...new Set(shown.slice(0, 300).flatMap((e) => e.view.endpoints).map(lower).filter(Boolean))], [shown]);
  const window = readWindow;
  const named = useMemo(() => [...new Set((sel.size ? shown.filter((e) => sel.has(e.id)) : cur ? [cur] : [])
    .flatMap((e) => e.view.sessions).map(lower).filter(Boolean))], [sel, shown, cur]);
  const { labels, machineFor } = useSessionIdentity({ map: t.lite.map, sessions, endpoints, window, named });

  // Everyone else who can be assigned, as [id, name, email]; the reader is
  // offered apart. A name the reader cannot list is the start of the email.
  const people = useMemo(() => [...invs.users.entries()]
    .filter(([id]) => id !== me)
    .map(([id, email]) => [id, invs.names?.get(lower(email)) || email.split("@")[0], email])
    .sort((a, b) => a[1].localeCompare(b[1])), [invs.users, invs.names, me]);

  // A decision on the selected cases, else the one in front, skipping any it
  // does not apply to where the team left them.
  const targets = useCallback((action) => (sel.size ? shown.filter((e) => sel.has(e.id)) : cur ? [cur] : []).filter((e) => canDecide(e, action)), [sel, shown, cur]);
  // `together` makes the cases one investigation: the one already being
  // investigated, else the first, takes the others' findings as duplicates.
  // `then` runs once the decision is saved.
  const decide = useCallback(async (action, opts = {}, { label, then } = {}) => {
    const list = targets(action === "together" ? "join" : action);
    if (!list.length || busy) return;
    if (!canWrite) { say("Your access can read the queue. Recording decisions needs alert triage write access."); return; }
    // Every decision but an assignment names the reader; none is sent before
    // the signed-in user is known.
    if (!me && action !== "assign") { say("Your sign-in is still loading. Try again in a moment."); return; }
    setBusy(true);
    setMode(null);
    const done = [];
    const lead = action === "together" ? list.find((e) => e.state === "investigating") || list[0] : null;
    try {
      if (lead) {
        if (lead.state === "new") done.push(...await invs.decide(lead.view, "take", { me }));
        for (const e of list) if (e !== lead) done.push(...await invs.join(e.view, lead.view, { me }));
      } else {
        for (const e of list) {
          if (action === "join") { if (e.id !== opts.target.id) done.push(...await invs.join(e.view, opts.target.view, { me })); }
          else done.push(...await invs.decide(e.view, action, { me, ...opts }));
        }
      }
      // A note is not a decision to take back.
      if (action !== "note") undoStack.current.push(done);
      // A case taken or assigned stays where it was, saying whose it is now;
      // any other decision lets it go.
      if (action !== "note") keepCases(action === "take" || action === "assign" ? list.map((e) => e.id) : lead ? [lead.id] : [], list.map((e) => e.id));
      if (undoStack.current.length > 20) undoStack.current.shift();
      setSel(new Set());
      // Nothing opens by itself: a decided open case closes.
      if (isOpen) setOpen(null);
      const what = list.length === 1 ? `“${list[0].title.length > 56 ? `${list[0].title.slice(0, 55)}…` : list[0].title}”` : plural(list.length, "case");
      const tune = action === "close" && opts.reason === "wrong" && list.length === 1 ? tuningOf(list[0].view)?.wrong || null : null;
      say(label ? label(what, list.length) : what, { undo: action !== "note", tune });
      then?.(list, done);
    } catch (error) {
      done.push(...(error?.done || []));
      if (done.length) undoStack.current.push(done);
      // A gateway that gave no answer of the server's says so, not its request line.
      const why = error?.serverMessage || (error?.status >= 500 ? `The server did not answer (${error.status})` : error?.message) || "The decision could not be saved";
      say(`${done.length ? "Saved part of it, then stopped. " : ""}${sentence(String(why).trim())} Try again.`, { error: true, undo: done.length > 0 });
    } finally {
      setBusy(false);
    }
  }, [targets, busy, canWrite, invs, me, isOpen, setOpen, say, tuningOf]);
  const undo = useCallback(async () => {
    const last = undoStack.current.pop();
    if (!last) { say("Nothing to undo"); return; }
    setBusy(true);
    const left = [...last];
    try {
      while (left.length) { await invs.revert(left[left.length - 1]); left.pop(); }
      say("Undone");
    } catch (error) {
      undoStack.current.push(left);
      say(`${error?.serverMessage || "The undo could not be saved."} Try again.`, { error: true, undo: true });
    } finally {
      setBusy(false);
    }
  }, [invs, say]);

  // A case's workspace, or one laid with several cases' findings.
  const workspace = useCallback((entry, list = null, investigationId = entry?.inv?.incidentId || null) => {
    if (!entry || !canInvestigate) return;
    const view = list?.length > 1
      ? caseView({ id: entry.id, title: entry.title, notes: [], related: [], matches: [] }, list.flatMap((e) => e.view.items))
      : entry.view;
    openInboxCase(view, { investigationId, titleOf: (s) => labels.get(s) || "" });
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set("mode", "workspace");
      next.delete("adminView"); next.delete("view"); next.delete("settingsView");
      return next;
    });
  }, [canInvestigate, labels, setParams]);
  // Explore, searching for something a case involved.
  const explore = useCallback((clause) => {
    if (!clause) return;
    setWorkspaceSearch(clause);
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set("mode", "explore");
      next.delete("adminView"); next.delete("view"); next.delete("settingsView");
      return next;
    });
  }, [setParams]);

  // Investigating: a case in triage becomes the reader's and its workspace
  // opens; one already being investigated opens its workspace for whoever
  // jumps in; several become one investigation, the reader's. A reader who
  // cannot record decisions only looks.
  const investigate = useCallback(() => {
    const open = (sel.size ? shown.filter((e) => sel.has(e.id)) : cur ? [cur] : []).filter((e) => isOpenState(e.state));
    if (!open.length) { if (cur && !sel.size) workspace(cur); return; }
    const lead = open.find((e) => e.state === "investigating") || open[0];
    // Cases joined to a teammate's investigation stay theirs; the reader jumps in.
    const theirs = lead.state === "investigating" && lead.owner !== me ? ownerOf(lead.owner) : null;
    if (!canWrite) workspace(lead, open);
    else if (open.length > 1) decide("together", {}, { label: theirs ? (w) => `Joined ${w} into ${theirs}'s investigation` : SAID.together, then: (_list, done) => workspace(lead, open, lead.inv?.incidentId || done[0]?.id) });
    else if (lead.state === "new") decide("take", {}, { label: SAID.investigate, then: (_list, done) => workspace(lead, null, done[0]?.id) });
    else workspace(lead);
  }, [sel, shown, cur, canWrite, me, ownerOf, decide, workspace]);
  // What a duplicate can join: the open investigations, those on the same
  // machine or under the same standard first.
  const joinTargets = useMemo(() => {
    const from = sel.size ? shown.filter((e) => sel.has(e.id)) : cur ? [cur] : [];
    if (!entries || !from.length) return [];
    const skip = new Set(from.map((e) => e.id));
    const machines = new Set(from.flatMap((e) => e.view.endpoints).map(lower));
    const standards = new Set(from.flatMap((e) => e.standards));
    return entries
      .filter((e) => e.inv && !skip.has(e.id) && e.state === "investigating")
      .map((e) => [e, (e.view.endpoints.some((x) => machines.has(lower(x))) ? 2 : 0) + (e.standards.some((x) => standards.has(x)) ? 1 : 0)])
      .sort((a, b) => b[1] - a[1] || (b[0].latestAt || 0) - (a[0].latestAt || 0))
      .slice(0, 9)
      .map(([e]) => e);
  }, [entries, sel, shown, cur]);
  // Another case, open in a filter that lists it (which then does not close
  // it as a filter the reader went to would).
  const goTo = useCallback((entry) => {
    const next = { ...filter, ...filterFor(entry, me) };
    lastFilter.current = filterKeyOf(next);
    setOpen(entry);
    setConsole(next);
  }, [filter, me, setOpen]);

  const openId = isOpen ? openEntry.id : null;
  // A row clicked stays where it was on screen, even as a case open above it
  // closes; a case opened from elsewhere (the time strip, a link) is scrolled to.
  const held = useRef(null);
  useLayoutEffect(() => {
    const h = held.current;
    held.current = null;
    const viewport = h?.row.isConnected ? h.row.closest("[data-overlayscrollbars-viewport]") : null;
    if (viewport) viewport.scrollTop += h.row.getBoundingClientRect().top - h.top;
    else if (openId) rootRef.current?.querySelector("[data-console-open]")?.scrollIntoView?.({ block: "start" });
  }, [openId]);

  const loadFailed = t.failed || ["error", "denied", "unsupported"].includes(t.conductState.status);
  // Until the team's decisions are read every case looks new, so the list
  // waits for them rather than show cases that then drop out of it.
  const reading = !t.read || !entries || invs.status === "loading";
  const nameOf = useCallback((id) => ownerLabel(id, invs.users, me), [invs.users, me]);
  // Each standard met, by the name a case says ("name vN"), to its instruction.
  const standardTexts = useMemo(() => new Map((t.liteStd?.lib?.items || []).map((s) => [`${s.name} v${s.version}`, instructionOf(s.text)])), [t.liteStd?.lib?.items]);
  const standardHelp = useCallback((name) => standardTexts.get(name) || "", [standardTexts]);
  const title = filterTitle(c.assignee, nameOf);
  // The console reads the newest flagged findings up to a limit; past it the
  // counts are the least there are, which All says without naming the rest.
  const clipped = !!t.conductState.clipped;
  // Where a clipped read stopped: the oldest finding it holds. Before it the
  // time strip hatches the range rather than show it clean.
  const readFrom = useMemo(() => {
    if (fixture) return fixture.readFrom ?? null;
    if (!clipped) return null;
    let oldest = Infinity;
    for (const r of t.conductState.rows || []) {
      const ms = parseServerTimestamp(r.completed_at);
      if (ms < oldest) oldest = ms;
    }
    return Number.isFinite(oldest) ? oldest : null;
  }, [fixture, clipped, t.conductState.rows]);
  // What review cleared, by why, while staff list it.
  const clearedCounts = useMemo(() => {
    if (!showCleared || !raw) return null;
    const by = new Map();
    for (const e of raw) if (e.state === "cleared") by.set(e.cleared, (by.get(e.cleared) || 0) + 1);
    return GROUP_ORDER.filter((g) => by.has(g)).map((g) => [g, by.get(g)]);
  }, [showCleared, raw]);
  // The assignee menu's options, counted once the list is read.
  const assigneeOptions = assignees || [...ASSIGNEES.map((a) => [a, ASSIGNEE_LABEL[a], null]), ...(ASSIGNEES.includes(c.assignee) ? [] : [[c.assignee, nameOf(c.assignee), null]])];
  // A row clicked opens its case (or closes it, open); its box selects it.
  const openRow = useCallback((e, row) => {
    held.current = row ? { row, top: row.getBoundingClientRect().top } : null;
    setOpen(openId === e.id ? null : e);
    setMode(null);
  }, [openId, setOpen]);
  const selectRow = useCallback((e) => setSel((s) => { const n = new Set(s); if (n.has(e.id)) n.delete(e.id); else n.add(e.id); return n; }), []);
  const cancel = useCallback(() => setMode(null), []);
  // Assign offers the reader, unless what it assigns is all theirs already.
  const selfAssign = !!me && targets("assign").some((e) => !(e.state === "investigating" && e.owner === me));
  // Each row's sentence, read once per list rather than on every keystroke,
  // and only where the reader may read session descriptions.
  const verdicts = t.liteShown.verdicts;
  const peeks = useMemo(() => (sensitive ? new Map(shown.map((e) => [e.id, peekOf(e, verdicts)])) : null), [sensitive, shown, verdicts]);

  return (
    <div ref={rootRef} className="relative flex h-full min-h-0 min-w-0 flex-1 flex-col" data-findings-console="">
      <header className="grid shrink-0 gap-[10px] pb-[10px] pt-[14px]">
        <div className="flex min-h-[32px] flex-wrap items-center gap-x-[20px] gap-y-[8px]">
          {/* Whose cases are listed is said once, by the heading, which picks it. */}
          <h1 className="m-0 min-w-0" data-console-assignee={c.assignee}>
            <Select
              value={c.assignee}
              onChange={(v) => setConsole({ assignee: v })}
              options={assigneeOptions.map(([id, label, n]) => ({ value: id, label: <>{label}{n == null ? null : <span className="ml-[10px] tabular-nums text-[color:var(--muted-soft)]" data-console-assignee-count="">{`${NUM.format(n)}${clipped ? "+" : ""}`}</span>}</> }))}
              title="Whose cases are listed"
              menuWidth={220}
              className="inline-flex max-w-full items-center gap-[10px] rounded-[7px] text-left text-[22px] font-semibold leading-[1.15] tracking-[-0.02em] text-[color:var(--ink)] transition-colors duration-150 hover:text-[color:var(--green)]"
              triggerContent={<><span className="truncate">{title}</span><Chevron open /></>}
            />
          </h1>
          <Checkbox checked={c.closed} onChange={(closed) => setConsole({ closed })} label="Show closed" data-console-closed="" />
          {staff ? (
            <span title="What review cleared, which customers never see">
              <Checkbox checked={showCleared} onChange={(cleared) => setConsole({ cleared })} label="Show cleared" data-console-cleared="" />
            </span>
          ) : null}
          {/* What to do with the selected cases, beside whose they are. */}
          {sel.size ? (
            <div className="fade-in ml-auto flex min-w-0 flex-wrap items-center gap-[6px] text-[12.5px]" data-console-selection="">
              <span className="font-medium text-[color:var(--ink)]">{plural(sel.size, "case")} selected</span>
              <div className="flex flex-1 flex-wrap items-center gap-[6px]" aria-busy={busy ? "true" : undefined} data-console-bar="">
                {liveMode && canWrite ? <ModeBar mode={liveMode} people={people} me={me} self={selfAssign} busy={busy} joinTargets={joinTargets} ownerOf={ownerOf} onDecide={decide} onMode={setMode} onCancel={cancel} /> : (
                  <>
                    {/* What triage does to one case it does to every selected
                        case it applies to; closed ones can be reopened. */}
                    {targets("close").length && (canWrite || canInvestigate) ? <button type="button" disabled={busy} className={PRIMARY} onClick={investigate} data-console-act="investigate">Investigate</button> : null}
                    {targets("close").length && canWrite ? <button type="button" disabled={busy} className={BUTTON} onClick={() => setMode("reject")} data-console-act="reject">Reject</button> : null}
                    {targets("assign").length && canWrite ? <button type="button" disabled={busy} className={BUTTON} onClick={() => setMode("assign")} data-console-act="assign">Assign</button> : null}
                    {targets("reopen").length && canWrite ? <button type="button" disabled={busy} className={BUTTON} onClick={() => decide("reopen", {}, { label: SAID.reopen })} data-console-act="reopen">Reopen</button> : null}
                    <button type="button" onClick={() => setSel(new Set())} className={`ml-[4px] ${QUIET}`} data-console-clear="">Clear</button>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-x-[16px] gap-y-[8px]">
        <Tabs
          className="mr-auto"
          label="Quick filters"
          value={c.quick}
          onChange={(q) => setConsole({ quick: q })}
          itemAttrs={(item) => ({
            "data-console-quick": item.id,
            title: item.id === "all" && clipped && !reading ? "Only the newest findings in this range were read. Narrow the range to see the rest." : undefined,
          })}
          items={QUICK.map((q) => ({
            id: q,
            label: (
              <span className="flex items-center gap-[7px]">
                {QUICK_LABEL[q]}
                <span className={`text-[11.5px] tabular-nums ${q === "critical" && quickCounts.critical ? "text-[color:var(--severity-high)]" : "text-[color:var(--muted-soft)]"}`} aria-busy={reading ? "true" : undefined}>{reading ? <Bar className="w-[14px]" /> : `${NUM.format(quickCounts[q])}${q === "all" && clipped ? "+" : ""}`}</span>
              </span>
            ),
          }))}
        />
        <div className="min-w-[260px] max-w-[560px] flex-[1_1_360px]" data-console-search="">
          <SearchInput
            size="sm"
            value={c.query}
            onChange={(query) => setConsole({ query })}
            placeholder="Search"
            label="Search findings"
            className="h-[32px] w-full"
            data-console-query=""
          />
        </div>
        <span className="w-[180px] shrink-0" data-console-sort={c.sort}>
          <Select value={c.sort} onChange={(v) => setConsole({ sort: v })} options={SORTS.map((s) => ({ value: s, label: SORT_LABEL[s] }))} ariaLabel="Sort" />
        </span>
        <span className="w-[160px] shrink-0" data-console-range="">
          <DateRangePicker range={range} onRangeChange={setRange} />
        </span>
        </div>
      </header>
      {/* What the reader should know of the list as read, when there is any. */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-[14px] gap-y-[4px] border-[color:var(--line)] text-[12px] text-[color:var(--muted)] [&:not(:empty)]:border-b [&:not(:empty)]:py-[8px]">
        {scopePath.length ? <span>Scoped to this selection</span> : null}
        {t.refreshing ? <span role="status" data-console-refreshing="">Showing the list from your last visit while checking for new findings.</span> : null}
        {!reading && clearedCounts ? <span data-console-cleared-counts="">Cleared by review: {clearedCounts.map(([g, n]) => `${GROUP_SHORT[g]} ${NUM.format(n)}`).join(", ") || "nothing in this range"}</span> : null}
        {!reading && t.ids?.sessionsCapped ? <span data-console-capped="">Only the newest {NUM.format(REVIEW_SESSION_CAP)} traces this selection reaches were read. Narrow it to read the rest.</span> : null}
        {!canWrite ? <span>You can read the queue. Recording decisions needs alert triage write access.</span> : null}
        {invs.status === "error" ? <span role="alert" className="text-[color:var(--severity-critical)]">Decisions could not be read, so every case shows as new. Reload to try again.</span> : null}
        {invs.capped ? <span>Showing decisions from the newest 1,000 saved cases.</span> : null}
        {t.liteStd.error ? <span role="alert" className="text-[color:var(--severity-critical)]">Review standards could not be loaded. Results may be incomplete.</span> : null}
      </div>
      <ScrollArea className="min-h-0 flex-1" contentClassName="pb-[48px] pr-[12px]" options={LIST_SCROLL}>
        {/* The strip is the page's range: its summary sticks at the list's top, and its chart scrolls up under it. */}
        <FindingsTime
          entries={shown}
          range={range}
          onRange={setRange}
          onOpenCase={(e) => { setOpen(e); setMode(null); }}
          onLight={setLit}
          reading={reading}
          readFrom={readFrom}
          fixture={fixture?.sessionsByHour || null}
        />
        <div role="list" aria-label={title} aria-busy={(reading || t.refreshing || t.loadingOlder) && !loadFailed ? "true" : undefined}>
          {loadFailed ? <p role="alert" className="m-0 py-[24px] text-[13px] text-[color:var(--severity-critical)]">The findings could not be read. Reload to try again.</p>
            : reading ? <><span className="sr-only">Reading the findings and what the team decided...</span><RowsLoading /></>
              : !shown.length ? <p className="fade-in m-0 py-[24px] text-[13px] text-[color:var(--muted)]">{c.query ? "Nothing in this view matches the search." : c.quick !== "all" ? "Nothing in this view matches the filter." : emptyText(c.assignee, c.closed, nameOf(c.assignee))}</p>
                : (
                  <Rows
                    shown={shown}
                    lit={lit}
                    openId={openId}
                    sel={sel}
                    sort={c.sort}
                    foldable={foldable}
                    folded={folded}
                    onFold={fold}
                    peeks={peeks}
                    terms={words}
                    ownerOf={ownerOf}
                    onOpen={openRow}
                    onSelect={selectRow}
                    body={(e) => (
                      <CaseBody
                        key={`body:${e.id}`}
                        entry={e}
                        headline={peeks?.get(e.id) || ""}
                        verdicts={verdicts}
                        terms={words}
                        access={access}
                        labels={labels}
                        machineFor={machineFor}
                        helpOf={standardHelp}
                        ownerOf={ownerOf}
                        range={range}
                        mode={liveMode}
                        people={people}
                        me={me}
                        selfAssign={selfAssign}
                        busy={busy}
                        canWrite={canWrite}
                        canInvestigate={canInvestigate}
                        canExplore={canExplore}
                        joinTargets={joinTargets}
                        joinedTo={e.joined ? entries.find((x) => x.inv?.incidentId === e.joined.id) || null : null}
                        onDecide={decide}
                        onMode={setMode}
                        onCancel={cancel}
                        onInvestigate={investigate}
                        messageAt={messageAt}
                        onGo={goTo}
                        onExplore={opensExplore ? explore : null}
                      />
                    )}
                  />
                )}
        </div>
      </ScrollArea>
      {toast ? (
        <div role="status" className={`fade-in absolute left-1/2 z-[5] flex max-w-[calc(100%-32px)] -translate-x-1/2 items-center gap-[12px] rounded-[10px] bg-[color:var(--ink)] py-[8px] pl-[14px] pr-[12px] text-[13px] text-[color:var(--bg)] shadow-[var(--node-shadow-lift)] ${isOpen ? "bottom-[64px]" : "bottom-[16px]"}`} data-console-toast="">
          <span>{toast.msg}</span>
          {toast.tune ? <button type="button" onClick={toast.tune} className="font-semibold">Tune the standard</button> : null}
          {toast.undo ? <button type="button" onClick={undo} className="font-semibold" data-console-undo="">Undo</button> : null}
        </div>
      ) : null}
    </div>
  );
}

// What an empty list says, by whose cases it lists.
function emptyText(assignee, closed, name) {
  if (assignee === "all") return closed ? "No findings in this range." : "No open findings in this range.";
  if (assignee === "none") return "Nothing to triage in this range.";
  if (assignee === "me") return closed ? "You held nothing in this range." : "You hold no open investigation in this range.";
  if (assignee === "any") return closed ? "No one held anything in this range." : "No one holds an open investigation in this range.";
  return `${name || "This teammate"} ${closed ? "held nothing" : "holds no open investigation"} in this range.`;
}

// The browser lays out and paints only the rows in view (content-visibility),
// so a list of thousands scrolls quickly; the open case is always laid out,
// for its pinned decision bar.
const OFFSCREEN = "[content-visibility:auto] [contain-intrinsic-size:auto_48px]";
// The list scrolls up and down only; what is wider scrolls on its own (a case's cards).
// Its content keeps clear of the overlaid scrollbar on the right.
const LIST_SCROLL = { overflow: { x: "hidden" } };

function Rows({ shown, lit, openId, sel, sort, foldable, folded, onFold, peeks, terms, ownerOf, onOpen, onSelect, body }) {
  const row = (e) => (
    <div key={e.id} role="listitem" className={e.id === openId ? undefined : OFFSCREEN}>
      <CaseRow entry={e} open={e.id === openId} lit={lit === e.id} sel={sel.has(e.id)} peek={peeks?.get(e.id) || ""} terms={terms} ownerOf={ownerOf} onOpen={onOpen} onSelect={onSelect} />
      {e.id === openId ? body(e) : null}
    </div>
  );
  if (sort !== "severity") return <div className="fade-in">{shown.map(row)}</div>;
  const groups = [];
  for (const e of shown) {
    const sev = e.severity || "other";
    if (!groups.length || groups[groups.length - 1].sev !== sev) groups.push({ sev, rows: [] });
    groups[groups.length - 1].rows.push(e);
  }
  const label = (g) => (
    <span className="flex items-baseline gap-[8px]">
      <span className={`${EYEBROW} capitalize`}>{g.sev}</span>
      <span className="text-[12px] tabular-nums text-[color:var(--muted-soft)]">{g.rows.length}</span>
    </span>
  );
  return <div className="fade-in">{groups.map((g) => {
    const shut = folded.has(g.sev);
    return (
      <Fragment key={g.sev}>
        {/* A section's chevron sits over its rows' chevrons, its name over their severity. */}
        <div className={`sticky top-[40px] z-[1] flex items-baseline bg-[color:var(--bg)] pb-[5px] pt-[10px] ${foldable ? "pl-[42px]" : "pl-[66px]"}`}>
          {foldable ? (
            <button type="button" aria-expanded={!shut} onClick={() => onFold(g.sev)} className="flex items-baseline gap-[12px] text-left [&_span]:transition-colors [&_span]:duration-150 [&:hover_span]:text-[color:var(--ink)]" data-console-section={g.sev}>
              <Chevron open={!shut} />
              {label(g)}
            </button>
          ) : label(g)}
        </div>
        {shut ? null : g.rows.map(row)}
      </Fragment>
    );
  })}</div>;
}

// What a decision waits on: why a case is rejected (then the analyst's
// words), which investigation a duplicate joins, who it goes to, or the words
// a true positive or a note is saved with, for the case open or for every
// selected case.
function ModeBar({ mode, people, me, self, busy, joinTargets, ownerOf, onDecide, onMode, onCancel }) {
  if (mode?.kind === "note") return <NoteStep key={mode.ask} mode={mode} busy={busy} onSave={(text, notChecked) => onDecide(mode.action, { ...mode.opts, text, ...(notChecked ? { notChecked } : {}) }, { label: mode.label })} onCancel={onCancel} />;
  if (mode === "assign") return <AssignStep people={people} me={me} self={self} busy={busy} onDecide={onDecide} onCancel={onCancel} />;
  const ask = mode === "join" ? (
    <>
      <span className={BAR_LABEL}>Duplicate of</span>
      {joinTargets.map((e) => (
        <button key={e.id} type="button" disabled={busy} className={BUTTON} onClick={() => onDecide("join", { target: e }, { label: SAID.join(e.title) })} data-console-join={e.id}>
          <span className="max-w-[240px] truncate">{e.title}</span>
          <span className="text-[color:var(--muted)]">{ownerOf(e.owner)}</span>
        </button>
      ))}
    </>
  ) : (
    <>
      <span className={BAR_LABEL}>Reject as</span>
      {/* A duplicate needs an open investigation to join. */}
      {REJECTS.filter((r) => r !== "duplicate" || joinTargets.length).map((r) => (
        <button key={r} type="button" disabled={busy} className={BUTTON} onClick={() => onMode(r === "duplicate" ? "join" : REJECT_NOTE[r])} data-console-reject={r}>{REJECT_LABEL[r]}</button>
      ))}
    </>
  );
  return (
    <>
      {ask}
      <span className="flex-1" />
      <button type="button" className={BUTTON} onClick={onCancel}>Cancel</button>
    </>
  );
}

// Who a case goes to: the reader, in one click (`self`, unless it is all
// theirs already), or a teammate searched for by name or address. Matches
// are suggested under the field as it is typed in; a click or Enter on one
// assigns it, and Enter alone assigns once what is typed names one person.
const FIELD = `${UI_FIELD} h-[32px]`;
// The bar sits at the bottom of the case, so the suggestions open upward.
const SUGGESTIONS = `absolute bottom-full left-0 z-[3] mb-[4px] grid max-h-[260px] w-[300px] list-none overflow-y-auto p-[4px] ${MENU}`;
const SUGGESTIONS_SHOWN = 8;
function AssignStep({ people, me, self, busy, onDecide, onCancel }) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [listOpen, setListOpen] = useState(false);
  const listId = useId();
  const term = q.trim().toLowerCase();
  const found = term ? people.filter(([, name, email]) => name.toLowerCase().includes(term) || email.toLowerCase().includes(term)) : [];
  const who = found.find(([, name, email]) => name.toLowerCase() === term || email.toLowerCase() === term) || (found.length === 1 ? found[0] : null);
  const shown = listOpen ? found.slice(0, SUGGESTIONS_SHOWN) : [];
  const current = shown[Math.min(active, shown.length - 1)] || null;
  const pick = (p) => { if (p && !busy) onDecide("assign", { owner: p[0] }, { label: (w) => (p[0] === me ? `You took ${w}` : `Assigned ${w} to ${p[1]}`) }); };
  const keys = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setListOpen(true);
      if (shown.length) setActive((i) => (Math.min(i, shown.length - 1) + (e.key === "ArrowDown" ? 1 : shown.length - 1)) % shown.length);
    } else if (e.key === "Enter") { e.preventDefault(); pick(current || who); }
    else if (e.key === "Escape") { e.preventDefault(); if (shown.length) setListOpen(false); else onCancel(); }
  };
  return (
    <>
      <span className={BAR_LABEL}>Assign to</span>
      {self ? (
        <>
          <button type="button" disabled={busy} className={PRIMARY} onClick={() => pick([me, "You"])} data-console-assign-me="">Me</button>
          <span className="text-[12.5px] text-[color:var(--muted)]">or</span>
        </>
      ) : null}
      <div className="relative">
        <input
          value={q}
          role="combobox"
          aria-expanded={shown.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={current ? `${listId}-${current[0]}` : undefined}
          autoComplete="off"
          onChange={(e) => { setQ(e.target.value); setActive(0); setListOpen(true); }}
          onFocus={() => setListOpen(true)}
          onBlur={() => setListOpen(false)}
          onKeyDown={keys}
          placeholder="Search by name or email"
          aria-label="Who to assign"
          className={`${FIELD} w-[240px]`}
          data-console-words=""
        />
        {shown.length ? (
          <ul id={listId} role="listbox" aria-label="Teammates" className={SUGGESTIONS} data-console-suggestions="">
            {shown.map((p) => (
              <li
                key={p[0]}
                id={`${listId}-${p[0]}`}
                role="option"
                aria-selected={p === current}
                // Pressing keeps the field's focus so its blur does not close the list first.
                onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                onMouseEnter={() => setActive(shown.indexOf(p))}
                className={`grid cursor-pointer gap-[1px] rounded-[6px] px-[8px] py-[5px] ${p === current ? "bg-[color-mix(in_srgb,var(--ink)_10%,transparent)]" : ""}`}
                data-console-suggestion={p[0]}
              >
                <span className="truncate text-[12.5px] text-[color:var(--ink)]">{p[1]}</span>
                <span className="truncate text-[11.5px] text-[color:var(--muted)]">{p[2]}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <button type="button" disabled={busy || !(current || who)} className={BUTTON} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(current || who)} data-console-assign-to="">Assign</button>
      {term && !found.length ? <span className="text-[12.5px] text-[color:var(--muted)]">{people.length ? "No one by that name." : "No one else can be assigned yet."}</span> : null}
      {found.length > SUGGESTIONS_SHOWN && listOpen ? <span className="text-[12.5px] text-[color:var(--muted)]">{`${NUM.format(found.length)} people match; keep typing`}</span> : null}
      <span className="flex-1" />
      <button type="button" className={BUTTON} onClick={onCancel}>Cancel</button>
    </>
  );
}

// The analyst's words for a decision: optional for a false positive or a true
// positive, the whole of a note. Enter saves, Esc puts it away.
const WORDS = `${FIELD} min-w-[200px] flex-1`;
function NoteStep({ mode, busy, onSave, onCancel }) {
  const [text, setText] = useState("");
  const [unchecked, setUnchecked] = useState("");
  const empty = !text.trim();
  const save = () => { if (!(mode.required && empty)) onSave(text.trim(), unchecked.trim()); };
  // Enter saves from either line, Tab goes between them, Esc puts it away.
  const keys = (e) => { if (e.key === "Enter") { e.preventDefault(); save(); } else if (e.key === "Escape") { e.preventDefault(); onCancel(); } };
  return (
    <>
      <span className={BAR_LABEL}>{mode.ask}</span>
      <input autoFocus value={text} maxLength={mode.notChecked ? 3000 : 3800} onChange={(e) => setText(e.target.value)} onKeyDown={keys} placeholder={mode.hint} aria-label={mode.hint} className={WORDS} data-console-words="" />
      {mode.notChecked ? <input value={unchecked} maxLength={800} onChange={(e) => setUnchecked(e.target.value)} onKeyDown={keys} placeholder="Not checked" aria-label="What was not checked" className={WORDS} data-console-not-checked="" /> : null}
      <button type="button" disabled={busy || (mode.required && empty)} className={PRIMARY} onClick={save} data-console-save="">Save</button>
      <button type="button" className={BUTTON} onClick={onCancel}>Cancel</button>
    </>
  );
}

function Entity({ label, children }) {
  return (
    <div className="grid gap-[2px]">
      <dt className={EYEBROW}>{label}</dt>
      <dd className="m-0 text-[13px] text-[color:var(--ink)]">{children}</dd>
    </div>
  );
}

function Section({ title, children, aside = null }) {
  return (
    <section className="grid gap-[8px]">
      <h2 className={`${EYEBROW} flex gap-[8px]`}>{title}{aside != null ? <span>{aside}</span> : null}</h2>
      {children}
    </section>
  );
}

// The open case's body, under its row: its story first (what happened, the
// standards it met, and who and what was involved; CaseStory.jsx), then where
// the team left it and what it did; under all, the decisions it can take.
function CaseBody({ entry, headline, verdicts, terms, access, labels, machineFor, helpOf, ownerOf, range, messageAt, mode, people, me, selfAssign, busy, canWrite, canInvestigate, canExplore, joinTargets, joinedTo, onDecide, onMode, onCancel, onInvestigate, onGo, onExplore }) {
  const lead = entry.view.items[0];
  const sensitive = access.status === "allowed";
  // The standards the case met, each with its instruction on hover; one that
  // met none says so (a rare one, why it is shown). None for what review cleared.
  const standards = entry.state === "cleared" ? null
    : entry.standards.length ? entry.standards.map((name) => ({ name, help: helpOf(name) || "" }))
    : [{ name: "No standard met", help: entry.view.origin === "rare" ? ONLY_SESSION_WHY : "", none: true }];
  const inv = entry.inv;
  const activity = useInvestigationActivity(inv?.incidentId || null, inv?.version || null);
  const reason = entry.reason || (entry.state === "closed" ? reasonFromNote(activity.activity.find((e) => readNote(e.note))?.note) : null);
  // Where the team left it, where that says more than that it is new.
  const stateText = entry.state === "new" && !entry.fresh ? "" : stateWords({ ...entry, reason }, ownerOf);
  const open = isOpenState(entry.state);
  const act = (key, label, onClick, cls = BUTTON) => <button key={key} type="button" disabled={busy} className={cls} onClick={onClick} data-console-act={key}>{label}</button>;
  const note = act("note", "Note", () => onMode(NOTE));

  let bar;
  if (!canWrite) {
    bar = (
      <>
        {canInvestigate ? act("investigate", "Investigate", onInvestigate, PRIMARY) : null}
        <span className="text-[12.5px] text-[color:var(--muted)]">You can read this case. Recording a decision needs alert triage write access.</span>
      </>
    );
  } else if (mode) {
    bar = <ModeBar mode={mode} people={people} me={me} self={selfAssign} busy={busy} joinTargets={joinTargets} ownerOf={ownerOf} onDecide={onDecide} onMode={onMode} onCancel={onCancel} />;
  } else if (entry.state === "cleared") {
    bar = (
      <>
        {act("reopen", "Reopen as a case", () => onDecide("reopen", {}, { label: (w) => `Reopened ${w} as a case, in triage` }), PRIMARY)}
        {canInvestigate ? act("investigate", "Investigate", onInvestigate) : null}
      </>
    );
  } else if (!open) {
    bar = (
      <>
        {act("reopen", "Reopen", () => onDecide("reopen", {}, { label: SAID.reopen }), PRIMARY)}
        {canInvestigate ? act("investigate", "Investigate", onInvestigate) : null}
        {note}
        {joinedTo ? <button type="button" onClick={() => onGo(joinedTo)} className="ml-[6px] max-w-[320px] truncate text-[12.5px] text-[color:var(--green)] hover:underline" data-console-joined={joinedTo.id}>{joinedTo.title}</button> : null}
      </>
    );
  } else if (entry.state === "new") {
    // Triage: dig in, close it, or hand it on; nothing is a true positive yet.
    bar = (
      <>
        {act("investigate", "Investigate", onInvestigate, PRIMARY)}
        {act("reject", "Reject", () => onMode("reject"))}
        {act("assign", "Assign", () => onMode("assign"))}
      </>
    );
  } else {
    bar = (
      <>
        <span className={BAR_LABEL}>Verdict</span>
        <div role="group" aria-label="Verdict" className={VERDICTS}>
          {act("tp", verdict("True positive", "bg-[color:var(--severity-critical)]"), () => onMode(TP_NOTE), SEGMENT)}
          {act("reject", verdict("Reject", "bg-[color:var(--severity-low)]"), () => onMode("reject"), SEGMENT)}
          {act("inconclusive", verdict("Inconclusive", "bg-[color:var(--severity-medium)]"), () => onMode(INCONCLUSIVE_NOTE), SEGMENT)}
        </div>
        <span className="flex-1" />
        {canInvestigate ? act("investigate", "Open investigation", onInvestigate) : null}
        {act("note", "Note", () => onMode(NOTE), QUIET)}
        {act("assign", "Reassign", () => onMode("assign"), QUIET)}
        {act("putBack", "Unassign", () => onDecide("putBack", {}, { label: SAID.putBack }), QUIET)}
      </>
    );
  }

  return (
    <section aria-label="Case" className="fade-in grid gap-[12px] border-b border-[color:var(--line)] bg-[color:var(--panel)] pb-[14px] pl-[66px] pr-[16px] pt-[2px]" data-console-reader={entry.id}>
      <CaseStory entry={entry} headline={headline} verdicts={verdicts} standards={standards} terms={terms} access={access} labels={labels} machineFor={machineFor} range={range} messageAt={messageAt} canWalk={canInvestigate} canExplore={canExplore} onExplore={onExplore} />
      {/* Where the team left the case and what it did, when there is any. */}
      {entry.state === "cleared" || stateText || inv ? (
        <div className="grid min-w-0 max-w-[96ch] content-start gap-[18px]">
          {entry.state === "cleared" || stateText ? (
            <dl className="m-0 flex flex-wrap gap-x-[32px] gap-y-[10px]" data-console-entities="">
              {entry.state === "cleared" ? <Entity label="Review">{GROUP_LABEL[entry.cleared] || "Cleared"}</Entity> : null}
              {stateText ? <Entity label="Status"><span data-console-state=""><Highlight text={stateText} terms={terms} /></span></Entity> : null}
            </dl>
          ) : null}
          {entry.state === "cleared" ? (
            <Section title="Why review cleared it">
              <p className="m-0 max-w-[68ch] text-[14px] leading-[1.55] text-[color:var(--ink)]"><Highlight text={`${GROUP_LABEL[entry.cleared] || "Cleared"}${sensitive && lead?.verdict?.why ? `. ${maskSecrets(lead.verdict.why)}` : "."}`} terms={terms} /></p>
            </Section>
          ) : null}
          {inv ? (
            <Section title="Activity">
              {activity.status === "error" ? <p className="m-0 text-[13px] text-[color:var(--muted)]">The activity could not be read.</p>
                  : (
                    <div className="grid" aria-busy={activity.status === "loading" ? "true" : undefined}>
                      {activity.status === "loading" && !activity.activity.length ? [0, 1].map((i) => (
                        <div key={i} className="grid grid-cols-[104px_minmax(0,1fr)] gap-[10px] py-[7px]"><Bar className="w-[80px]" /><Bar className={i ? "w-[40%]" : "w-[60%]"} /></div>
                      )) : null}
                      {activity.activity.map((e) => [e, activityLine(e, ownerOf)]).filter(([, line]) => line).map(([e, line]) => (
                        <div key={e.operationId} className="grid grid-cols-[104px_minmax(0,1fr)] gap-[10px] py-[4px] text-[12.5px]">
                          <span className="whitespace-nowrap font-mono text-[11px] text-[color:var(--muted-soft)]">{stamp(e.atMs)}</span>
                          <ActivityLine entry={e} line={line} terms={terms} ownerOf={ownerOf} />
                        </div>
                      ))}
                    </div>
                  )}
            </Section>
          ) : null}
        </div>
      ) : null}
      {/* The decisions stay in reach while the case is read: the bar holds the
          bottom of the list until the case's own end scrolls up to it. */}
      <div className="sticky bottom-0 z-[2] flex flex-wrap items-center gap-[6px] border-t border-[color:var(--line)] bg-[color:var(--panel)] pb-[2px] pt-[10px]" aria-busy={busy ? "true" : undefined} data-console-bar="">{bar}</div>
    </section>
  );
}

// One activity entry: who did what, and what they wrote with it.
function ActivityLine({ entry, line, terms, ownerOf }) {
  const { says, words, notChecked } = line;
  return (
    <span className="min-w-0 [overflow-wrap:anywhere]">
      <span className="font-medium">{ownerOf(entry.actorUserId) || "Someone"}</span> {says}
      {words ? <span className="mt-[2px] block text-[color:var(--muted)]">{"\u201c"}<Highlight text={words} terms={terms} />{"\u201d"}</span> : null}
      {notChecked ? <span className="mt-[2px] block text-[color:var(--muted)]" data-console-not-checked-said="">Not checked: {notChecked}</span> : null}
    </span>
  );
}
